import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import { useHistory } from 'react-router-dom';

import { withState } from '../../../../components/common/grid/WrappedGrid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { API_URL, ORDER_STATUS_COLORS } from '../../../../utils/constants';
import { CreatedOnCell } from '../../../../components/common/grid/Cells';
import { DateRangeFilterCell } from '~/src/components/common/grid/DateRangeFilterCell';
import GridWrapper from '~/src/components/common/GridWrapper';
import WarehousesModal from './orderReserve/components/WarehousesModal';

import createIcon from '../../../../static/img/createIcon.svg';
import { InputFilterCell } from '~/src/components/common/grid/InputFilterCell';
import styled from 'styled-components';
import { WarehouseGetDto } from 'svila-common/types/back-end/warehouseGetDTO';
import PurchaseOrdersApi from '~/src/services/api/PurchaseOrdersApi';
import TableButton from '~/src/components/common/forms/Button/TableButton';

const StatusDot = styled.span<{ color: string }>`
  height: 13px;
  width: 13px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  border: 1px solid grey;
  display: inline-block;
  margin-right: 8px;
`;

export default function PurchaseOrdersList(): React.ReactElement {
  const stateRef = useRef(null);
  const url = `${API_URL}/purchase-orders/get`;
  const StatefullGrid = React.useCallback(withState(Grid, url), [url]);
  const history = useHistory();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [warehouses, setWarehouses] = React.useState<WarehouseGetDto[]>([]);
  const [loading, setLoading] = React.useState(false);

  const handleShowWarehouses = async (orderId: string) => {
    setLoading(true);
    try {
      const response = await PurchaseOrdersApi.getWarehoueseForPurchaseOrder(orderId);
      setWarehouses(response.data);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const WarehousesCell = (row) => {
    const { dataItem } = row;
    return (
      <td>
        <TableButton
          type='button'
          onClick={() => handleShowWarehouses(dataItem.id)}
          title="View Warehouses"
        />
      </td>
    );
  };

  const ProductNameCell = (row) => {
    const { dataItem } = row;
    return (
      <td>
        <Link to={`/purchase-orders/edit/${dataItem.id}`}>{dataItem?.supplierName}</Link>
      </td>
    );
  };

  const WarehouseOwnerCell = (row) => {
    const { dataItem } = row;
    return (
      <td>
        <Link to={`/purchase-orders/edit/${dataItem.id}`}>{dataItem?.warehouseOwnerName}</Link>
      </td>
    );
  };

  const PurchaseOrderStatusCell = (row) => {
    const { dataItem } = row;
    const status = dataItem?.purchaseOrderStatus;
    const statusKey = status.replace(/\s+/g, '') as keyof typeof ORDER_STATUS_COLORS;
    const color = ORDER_STATUS_COLORS[statusKey] || '#000';
    return (
      <td>
        <Link to={`/purchase-orders/edit/${dataItem.id}`}>
          <StatusDot color={color} />
          {status}
        </Link>
      </td>
    );
  };

  return (
    <WhitePad>
      <TitleComponent
        title="Purchase Orders"
        titleRight="Create"
        onRightHandler={(): void => history.push('/purchase-orders/create')}
        iconRight={createIcon}
      // onRightHandler={history.push('/purchase-orders/create')}
      />
      <GridWrapper>
        <StatefullGrid ref={stateRef}>
          <GridColumn field="createdOn" title="Date" cell={CreatedOnCell} filterCell={DateRangeFilterCell} />
          <GridColumn field="warehouseOwnerName" title="Warehouse Owner" cell={(row) => WarehouseOwnerCell(row)} />
          <GridColumn field="supplierName" title="Supplier" cell={(row) => ProductNameCell(row, stateRef)} />
          <GridColumn field="orderNumber" title="Order Number" />
          <GridColumn field="shippingType" title="Shipping Type" />
          <GridColumn
            field="warehouses"
            title="Warehouses"
            cell={WarehousesCell} />
          <GridColumn
            field="sellOrderStatus"
            title="Status"
            cell={(row) => PurchaseOrderStatusCell(row, stateRef)}
            sortable={false}
            filterable={false}
          />
        </StatefullGrid>
      </GridWrapper>
      <WarehousesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        warehouses={warehouses}
        loading={loading}
      />
    </WhitePad>
  );
}
